// frontend/shared/src/poll.ts —— spec §2 poll；TD-838：页面不可见即暂停，回前台立即执行一次
export interface VisibilitySource {
  isHidden(): boolean;
  subscribe(fn: () => void): () => void;
}

export const documentVisibility: VisibilitySource = {
  isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  subscribe: (fn) => {
    if (typeof document === 'undefined') return () => undefined;
    document.addEventListener('visibilitychange', fn);
    return () => document.removeEventListener('visibilitychange', fn);
  },
};

export interface Poller {
  start(): void;
  stop(): void;
  readonly running: boolean;
}

// schedule 在隐藏时不排程；回前台 schedule(0) 立即执行；tick 在途（busy）时不重入、到期顺延
export function createPoller(tick: () => Promise<unknown>, intervalMs: number, vis: VisibilitySource = documentVisibility): Poller {
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) throw new Error(`轮询间隔非法：${intervalMs}`);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  let busy = false;
  let unsub: (() => void) | null = null;

  const schedule = (ms: number): void => {
    if (timer) clearTimeout(timer);
    timer = running && !vis.isHidden() ? setTimeout(run, ms) : null;
  };
  const run = (): void => {
    timer = null;
    if (!running || vis.isHidden()) return;
    if (busy) {
      schedule(intervalMs);
      return;
    }
    busy = true;
    let p: Promise<unknown>;
    try {
      p = tick();
    } catch (e) {
      p = Promise.reject(e); // 同步抛错与异步拒绝同样处理，不终止轮询
    }
    p.catch((e: unknown) => console.error('[gp:poll]', e)).finally(() => {
      busy = false;
      schedule(intervalMs);
    });
  };
  return {
    start() {
      if (running) return;
      running = true;
      unsub = vis.subscribe(() => schedule(0));
      schedule(0);
    },
    stop() {
      running = false;
      if (timer) clearTimeout(timer);
      timer = null;
      unsub?.();
      unsub = null;
    },
    get running() {
      return running;
    },
  };
}
